import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bell, X, Check, CheckCheck, Trash2, 
  Sparkles, AlertCircle, Info, CheckCircle,
  Users, MapPin, Film, Camera, FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNotifications, NOTIFICATION_TYPES, NOTIFICATION_CATEGORIES } from '@/contexts/NotificationContext';

const typeIcons = {
  [NOTIFICATION_TYPES.SUCCESS]: { icon: CheckCircle, color: 'text-[var(--color-teal-400)]' },
  [NOTIFICATION_TYPES.ERROR]: { icon: AlertCircle, color: 'text-[var(--color-error)]' },
  [NOTIFICATION_TYPES.INFO]: { icon: Info, color: 'text-[var(--color-gold-400)]' },
  [NOTIFICATION_TYPES.WARNING]: { icon: AlertCircle, color: 'text-[var(--color-warning)]' },
  [NOTIFICATION_TYPES.GENERATION]: { icon: Sparkles, color: 'text-[var(--color-gold-400)]' }
};

const categoryIcons = {
  [NOTIFICATION_CATEGORIES.CHARACTERS]: Users,
  [NOTIFICATION_CATEGORIES.LOCATIONS]: MapPin,
  [NOTIFICATION_CATEGORIES.SCENES]: Film,
  [NOTIFICATION_CATEGORIES.STORYBOARD]: Camera,
  [NOTIFICATION_CATEGORIES.SCRIPT]: FileText,
  [NOTIFICATION_CATEGORIES.GENERAL]: Bell
};

function formatTimeAgo(date) {
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function NotificationItem({ notification, onMarkRead, onDelete }) {
  const TypeIcon = typeIcons[notification.type]?.icon || Info;
  const typeColor = typeIcons[notification.type]?.color || 'text-[var(--text-muted)]';
  const CategoryIcon = categoryIcons[notification.category] || Bell;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className={`p-3 border-b border-[var(--border-subtle)] hover:bg-[var(--bg-hover)] transition-colors ${
        !notification.read ? 'bg-[rgba(212,168,83,0.05)]' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Type Icon */}
        <div className={`mt-0.5 ${typeColor}`}>
          <TypeIcon size={16} />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium text-[var(--text-primary)]">
              {notification.title}
            </span>
            {!notification.read && (
              <span className="w-2 h-2 rounded-full bg-[var(--color-gold-500)]" />
            )}
          </div>
          
          <p className="text-xs text-[var(--text-muted)] line-clamp-2">
            {notification.message}
          </p>
          
          <div className="flex items-center gap-2 mt-2">
            <CategoryIcon size={10} className="text-[var(--text-disabled)]" />
            <span className="text-[10px] text-[var(--text-disabled)]">
              {notification.category}
            </span>
            <span className="text-[10px] text-[var(--text-disabled)]">
              {formatTimeAgo(notification.timestamp)}
            </span>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {!notification.read && (
            <button
              onClick={() => onMarkRead(notification.id)}
              className="p-1 rounded hover:bg-[var(--bg-muted)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              title="Mark as read"
            >
              <Check size={12} />
            </button>
          )}
          <button
            onClick={() => onDelete(notification.id)}
            className="p-1 rounded hover:bg-[var(--bg-muted)] text-[var(--text-muted)] hover:text-[var(--color-error)]"
            title="Delete"
          >
            <X size={12} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default function NotificationPanel({ isOpen, onClose, onNavigate }) {
  const { 
    notifications, 
    unreadCount, 
    markAsRead, 
    markAllAsRead, 
    clearNotification,
    clearAll 
  } = useNotifications();
  
  const [filter, setFilter] = useState('all'); // all, unread
  
  const filteredNotifications = filter === 'unread' 
    ? notifications.filter(n => !n.read)
    : notifications;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40"
          />
          
          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-96 bg-[var(--bg-surface)] border-l border-[var(--border-default)] z-50 flex flex-col shadow-2xl"
          >
            {/* Header */}
            <div className="p-4 border-b border-[var(--border-subtle)] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell size={18} className="text-[var(--color-gold-400)]" />
                <h2 className="font-semibold text-[var(--text-primary)]">Notifications</h2>
                {unreadCount > 0 && (
                  <span className="px-1.5 py-0.5 bg-[var(--color-gold-500)] text-[var(--color-base-950)] text-[10px] font-bold rounded-full">
                    {unreadCount}
                  </span>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-[var(--bg-hover)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              >
                <X size={18} />
              </button>
            </div>
            
            {/* Filter tabs */}
            <div className="px-4 py-2 border-b border-[var(--border-subtle)] flex gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  filter === 'all' 
                    ? 'bg-[rgba(212,168,83,0.2)] text-[var(--color-gold-400)]' 
                    : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('unread')}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  filter === 'unread' 
                    ? 'bg-[rgba(212,168,83,0.2)] text-[var(--color-gold-400)]' 
                    : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                }`}
              >
                Unread ({unreadCount})
              </button>
            </div>
            
            {/* Actions bar */}
            {notifications.length > 0 && (
              <div className="px-4 py-2 border-b border-[var(--border-subtle)] flex justify-between">
                <button
                  onClick={markAllAsRead}
                  disabled={unreadCount === 0}
                  className="flex items-center gap-1 text-[10px] text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-50"
                >
                  <CheckCheck size={12} />
                  Mark all read
                </button>
                <button
                  onClick={clearAll}
                  className="flex items-center gap-1 text-[10px] text-[var(--text-muted)] hover:text-[var(--color-error)]"
                >
                  <Trash2 size={12} />
                  Clear all
                </button>
              </div>
            )}
            
            {/* Notifications list */}
            <ScrollArea className="flex-1">
              {filteredNotifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-12 h-12 rounded-full bg-[var(--bg-muted)] flex items-center justify-center mb-3">
                    <Bell size={24} className="text-[var(--text-disabled)]" />
                  </div>
                  <p className="text-sm text-[var(--text-muted)]">
                    {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
                  </p>
                  <p className="text-xs text-[var(--text-disabled)] mt-1">
                    Generation updates will appear here
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-[var(--border-subtle)]">
                  <AnimatePresence>
                    {filteredNotifications.map(notification => (
                      <div key={notification.id} className="group">
                        <NotificationItem
                          notification={notification}
                          onMarkRead={markAsRead}
                          onDelete={clearNotification}
                        />
                      </div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </ScrollArea>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// Notification bell icon with badge component (renders as div, not button)
export function NotificationBell() {
  const { unreadCount } = useNotifications();
  
  return (
    <div 
      className="relative"
      data-testid="notification-bell"
    >
      <Bell size={20} />
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-[var(--color-error)] text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 animate-pulse">
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </div>
  );
}
